import type { PageOrientation } from '@record-fill';
import type { FormElement } from '../model/form.types';
import { applyHeaderRecordFields } from './form-header-fields';
import { serializeForm, type FormDefinition } from './form-serializer';

export const APPROVAL_CHANGELOG_MAX_LENGTH = 1500;

// Matched by name because record type ids are per branch and change on every re-seed. The backend
// keeps the same constant (RECORD_TYPE_EDITABLE_IF_API_NAME) and enforces the same rule.
export const RECORD_TYPE_EDITABLE_IF_API_NAME = 'Editável (IF/API)';

export type FunctionalInspectionType = 'NEW_DEVICE' | 'ASSISTENCE_DEVICE';

export const FUNCTIONAL_INSPECTION_TYPE_OPTIONS: readonly {
  value: FunctionalInspectionType;
  label: string;
}[] = [
  { value: 'NEW_DEVICE', label: 'Aparelho Novo' },
  { value: 'ASSISTENCE_DEVICE', label: 'Aparelho de Assistência Técnica' },
];

export function requiresFunctionalInspectionType(recordTypeName: string): boolean {
  return recordTypeName === RECORD_TYPE_EDITABLE_IF_API_NAME;
}

export interface ApprovalConfigValues {
  moduleId: number | '';
  recordOriginId: number | '';
  recordTypeId: number | '';
  /** Only decides the IF/API rule client-side; it is never sent to the API. */
  recordTypeName: string;
  functionalInspectionType: FunctionalInspectionType | '';
}

export interface ApprovalFormValues extends ApprovalConfigValues {
  prefix: string;
  title: string;
  rev: string;
  approvalManagerId: number | '';
  changelog: string;
}

export interface SendMatrixToApprovalDto {
  /** Set when promoting an existing draft, so the backend flips that row instead of duplicating it. */
  matrixId?: number;
  prefix: string;
  title: string;
  approvalManagerId: number;
  changelog?: string;
  moduleId: number;
  recordOriginId: number;
  recordTypeId: number;
  functionalInspectionType?: FunctionalInspectionType;
  matrixStructure: FormDefinition;
  rulesStructure?: object | null;
  permissionsStructure?: object | null;
}

export interface BuildSendToApprovalPayloadArgs {
  values: ApprovalFormValues;
  elements: FormElement[];
  rulesStructure: object | null;
  permissionsStructure: object | null;
  orientation: PageOrientation;
  /** The draft being edited, when in edit mode. */
  matrixId?: number | null;
}

function isPositiveId(value: number | ''): boolean {
  const id = Number(value);
  return Number.isFinite(id) && id > 0;
}

export function isApprovalFormComplete(values: ApprovalFormValues): boolean {
  return (
    values.prefix.trim().length > 0 &&
    values.title.trim().length > 0 &&
    values.rev.trim().length > 0 &&
    isPositiveId(values.approvalManagerId) &&
    isPositiveId(values.moduleId) &&
    isPositiveId(values.recordOriginId) &&
    isPositiveId(values.recordTypeId) &&
    (!requiresFunctionalInspectionType(values.recordTypeName) ||
      values.functionalInspectionType !== '')
  );
}

export function buildSendToApprovalPayload({
  values,
  elements,
  rulesStructure,
  permissionsStructure,
  orientation,
  matrixId,
}: BuildSendToApprovalPayloadArgs): SendMatrixToApprovalDto {
  const prefix = values.prefix.trim();
  const title = values.title.trim();
  const rev = values.rev.trim();
  const changelog = values.changelog.trim();
  // REV is persisted only inside the header element: the listing revision is computed server-side
  // and the DTO has no rev key.
  const serialized = serializeForm(applyHeaderRecordFields(elements, { prefix, rev, title }), {
    prefix,
    title,
    orientation,
  });
  const sendsInspectionType =
    requiresFunctionalInspectionType(values.recordTypeName) &&
    values.functionalInspectionType !== '';

  return {
    ...(matrixId != null && { matrixId }),
    prefix,
    title,
    approvalManagerId: Number(values.approvalManagerId),
    ...(changelog ? { changelog } : {}),
    moduleId: Number(values.moduleId),
    recordOriginId: Number(values.recordOriginId),
    recordTypeId: Number(values.recordTypeId),
    ...(sendsInspectionType && {
      functionalInspectionType: values.functionalInspectionType as FunctionalInspectionType,
    }),
    matrixStructure: JSON.parse(serialized) as FormDefinition,
    rulesStructure,
    permissionsStructure,
  };
}
